import { LIVE_OWN_ROOM, useLiveRoom } from "@nexxt/app-kit";
import { toast } from "@nexxt/ui";
import { type AnyRouter, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { m } from "../../../paraglide/messages.js";
import { useCan } from "../auth/me";
import { coalesced } from "../send/send-live";
import { type Notice, readNotices } from "./hot.queries";

// The toasts of the bell's phase 1 (ADR-316 §5): crm tells the own room of the person (`/live/me`) after the commit of
// a notice, and the screen reads the notices after its cursor at once, a toast each, with the link to the account (or
// to the send the breaker paused). The first read only takes the cursor, so a tab opened later shows nothing of
// before. No timer: the reserve read of ADR-278 only while the socket is down. Never "is watching now".

/** What a notice says, in the words of Guestlist (ADR-306 §5). */
function noticeText(notice: Notice): string {
  const account = notice.account?.name || m.customer_unknown();
  switch (notice.kind) {
    case "clicked":
      return m.notice_clicked({ account });
    case "link_opened":
      return m.notice_link_opened({ account });
    case "slots_viewed":
      return m.notice_slots_viewed({ account });
    case "booked":
      return m.notice_booked({ account });
    case "send_paused":
      return m.notice_send_paused({ send: notice.send?.name ?? "" });
    case "summary":
      return m.notice_summary();
  }
}

/** Where the action of a toast leads: the send the breaker paused, the account, or Today's "Hot right now". */
function openNotice(router: AnyRouter, notice: Notice): void {
  if (notice.send) void router.navigate({ to: "/sends/$sendId", params: { sendId: notice.send.id } });
  else if (notice.account) {
    void router.navigate({ to: "/accounts/$organizationId", params: { organizationId: notice.account.id } });
  } else void router.navigate({ to: "/" });
}

function showNotice(router: AnyRouter, notice: Notice): void {
  const options = {
    ...(notice.campaign && { description: notice.campaign.name }),
    action: {
      label: notice.send || notice.account ? m.notice_open() : m.notice_see_hot(),
      onClick: () => openNotice(router, notice),
    },
  };
  if (notice.kind === "send_paused") toast.warning(noticeText(notice), options);
  else toast.info(noticeText(notice), options);
}

/**
 * The reads of the toasts of one tab: the cursor and the toasts already shown live here, never in React state, and
 * the reads after a signal do not overlap (`coalesced`).
 */
function noticeReader(router: AnyRouter) {
  let cursor: string | null = null;
  const shown = new Set<string>();
  return {
    /** The first read: the cursor alone. */
    start: async () => {
      cursor = (await readNotices(null)).cursor;
    },
    read: coalesced(async () => {
      if (cursor === null) return;
      const answer = await readNotices(cursor);
      for (const notice of answer.items) {
        if (shown.has(notice.id)) continue;
        shown.add(notice.id);
        showNotice(router, notice);
      }
      cursor = answer.cursor;
    }),
  };
}

/** Mounted once in the frame: listens to the own room while the person may read the toasts. */
export function NoticeToasts() {
  const can = useCan("crm.campaign.read");
  // the router does not change: the reader made once keeps it
  const router = useRouter();
  const [reader] = useState(() => noticeReader(router));
  const [started, setStarted] = useState(false);

  // the first read takes the cursor; the room opens after it, so its first catch-up reads what came since. A first
  // read that fails (a blip at load) is tried again when the window comes back to focus or online, never on a timer
  useEffect(() => {
    if (!can || started) return;
    let mounted = true;
    const stopWaiting = () => {
      window.removeEventListener("focus", start);
      window.removeEventListener("online", start);
    };
    function start() {
      stopWaiting();
      reader
        .start()
        .then(() => mounted && setStarted(true))
        .catch(() => {
          if (!mounted) return;
          window.addEventListener("focus", start);
          window.addEventListener("online", start);
        });
    }
    start();
    return () => {
      mounted = false;
      stopWaiting();
    };
  }, [can, started, reader]);

  useLiveRoom(started ? LIVE_OWN_ROOM : undefined, [], { read: reader.read });
  return null;
}
